import { Editor } from 'domains/reporter/RichTextEditor/core';
import type { RecorderTarget } from 'common/Recorder/useRecorder';
import type { CreateEnhanceProvider } from '../../types';
import type { ErrorResponse } from './ASRPlex/ASRPlexProtocol';
import type { NvoqError } from './NvoqQueue/createNvoqWebsocketFactory';
import type { DictationPluginPropertyOptions, RecorderVoiceCommandProps } from './types';

import { ASRPlexDictation } from './ASRPlex/ASRPlexDictation';
import { NvoqQueueProvider } from './NvoqQueue/useNvoqQueue';

import { useRecorder } from 'common/Recorder/useRecorder';
import { useToasterDispatch } from 'common/ui/Toaster';
import { NVOQ_WS_MAXIMUM_CONNECTIONS } from 'config';
import { NameSpaces, sendCrossTabEvent } from 'modules/EventsManager';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { logger } from 'modules/logger';
import { useCallback } from 'react';
import type { SlateProps } from 'slate-react';
import { useFocusMode } from 'hooks/useFocusMode';
import { telemetryContextFor } from 'modules/Apollo/types';
import { PicklistProvider } from 'domains/reporter/Template/usePicklist';

const TOAST_ERROR_MESSAGE =
  'There was an error processing dictation. Please refresh the page and try again. If problems persist, please contact Sirona support.';
const DICTATION_ERROR_TOAST_KEY_PREFIX = 'dictation-processing-error';
const ALWAYS_EDITABLE_TARGETS: ReadonlyArray<RecorderTarget> = [
  'templateEditor',
  'editMacroEditTab',
  'editMacroPreviewTab',
  'newMacro',
];

type ProviderComponentProps = Readonly<{
  editor: Editor;
  nvoqID: string;
  nvoqAuthorization: string;
  /**
   * Important! Must be unique per editor instantiated in the app.
   */
  target: RecorderTarget;
  children: React.ReactNode;
  enablePicklistDictation: boolean;
  // asr-plex
  getActiveTemplateId?: () => string | undefined;
}>;

const ProviderComponent = ({
  children,
  editor,
  nvoqAuthorization,
  nvoqID,
  target,
  enablePicklistDictation,
  getActiveTemplateId,
  ...rest
}: ProviderComponentProps): React.ReactElement => {
  const { isRecording, stopRecording, recorderTarget } = useRecorder();
  const { enqueueOrUpdateToast } = useToasterDispatch();
  const dictationErrorToastKey = `${DICTATION_ERROR_TOAST_KEY_PREFIX}-${target}`;

  const showDictationErrorToast = useCallback(() => {
    enqueueOrUpdateToast(TOAST_ERROR_MESSAGE, dictationErrorToastKey, {
      severity: 'error',
    });
  }, [dictationErrorToastKey, enqueueOrUpdateToast]);

  const onExternalSubstitution = useCallback((props: RecorderVoiceCommandProps) => {
    sendCrossTabEvent(NameSpaces.RECORDER_VOICE_COMMAND, props);
  }, []);

  type ErrorMessage =
    | ErrorResponse
    | {
        msg: NvoqError;
      }
    | undefined;
  type MessageExtractor = (arg1: ErrorMessage) => Nullable<string>;
  function errorHandlerFactory(messageExtractor: MessageExtractor) {
    return (e: ErrorMessage) => {
      showDictationErrorToast();

      const logMessage = `Dictation Service Error: ${messageExtractor(e) ?? 'Unknown error'}`;
      logger.error(TOAST_ERROR_MESSAGE);
      logger.error(logMessage);

      if (isRecording) {
        stopRecording();
      }
    };
  }

  const isEnabled = isRecording && target === recorderTarget;
  const isAlwaysEditable = ALWAYS_EDITABLE_TARGETS.includes(target);

  const baseProps = {
    editor,
    isEnabled,
    isAlwaysEditable,
    enablePicklistDictation,
    onExternalSubstitution,
    onWebsocketError: () => {
      showDictationErrorToast();
      logger.error(TOAST_ERROR_MESSAGE);

      if (isRecording) {
        stopRecording();
      }
    },
  } as const;
  const [asrPlexFeatureEnabled] = useFeatureFlagEnabled(FF.REPORTER_ASR_PLEX);
  const { isFocusModeSettingVisible } = useFocusMode({ context: APOLLO_QUERY_CONTEXT });

  // When Focus Mode is visible but ASR Plex is NOT flagged on, the focus-mode toggle dynamically
  // switches dictation between ASR Plex (toggle on) and nVoq (toggle off), so both providers must
  // be mounted. If ASR Plex IS flagged on, it owns dictation for both toggle states (see
  // checkShouldUseASRPlex), so the NvoqQueueProvider would never be used — don't render it.
  if (isFocusModeSettingVisible && !asrPlexFeatureEnabled) {
    return (
      <ASRPlexDictation
        {...baseProps}
        onError={errorHandlerFactory((e) => (e as ErrorResponse)?.payload?.message)}
        getActiveTemplateId={getActiveTemplateId}
      >
        <NvoqQueueProvider
          {...baseProps}
          maxNumberOfConnections={NVOQ_WS_MAXIMUM_CONNECTIONS}
          nvoqAuthorization={nvoqAuthorization}
          nvoqID={nvoqID}
          onNvoqError={errorHandlerFactory((e) => (e as { msg: NvoqError })?.msg?.error?.message)}
        >
          {children}
        </NvoqQueueProvider>
      </ASRPlexDictation>
    );
  }
  if (asrPlexFeatureEnabled) {
    return (
      <ASRPlexDictation
        {...baseProps}
        onError={errorHandlerFactory((e) => (e as ErrorResponse)?.payload?.message)}
        getActiveTemplateId={getActiveTemplateId}
      >
        {children}
      </ASRPlexDictation>
    );
  }
  return (
    <NvoqQueueProvider
      {...baseProps}
      maxNumberOfConnections={NVOQ_WS_MAXIMUM_CONNECTIONS}
      nvoqAuthorization={nvoqAuthorization}
      nvoqID={nvoqID}
      onNvoqError={errorHandlerFactory((e) => (e as { msg: NvoqError })?.msg?.error?.message)}
    >
      {children}
    </NvoqQueueProvider>
  );
};

export const enhanceProviderDictation: CreateEnhanceProvider<DictationPluginPropertyOptions> =
  ({ pluginID, nvoqAuthorization, nvoqID, target, enablePicklistDictation, getActiveTemplateId }) =>
  (Component: React.ComponentType<SlateProps>) =>
    function EnhanceProviderDictation(props: SlateProps) {
      return (
        <Component {...props}>
          <PicklistProvider>
            <ProviderComponent
              nvoqAuthorization={nvoqAuthorization}
              nvoqID={nvoqID}
              editor={props.editor}
              target={target}
              enablePicklistDictation={enablePicklistDictation}
              getActiveTemplateId={getActiveTemplateId}
            >
              {props.children}
            </ProviderComponent>
          </PicklistProvider>
        </Component>
      );
    };

ProviderComponent.displayName = 'EnhanceProviderDictation';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(ProviderComponent);
